<script setup lang="ts">
import { repository } from "~/repository";
import type { Article } from "~/types";
import { useInfiniteScroll } from "@vueuse/core";
import AutoComplete, {
  type AutoCompleteCompleteEvent,
} from "primevue/autocomplete";
import { getDeviceType, handleApiError } from "~/utils/common";

useSeoMeta({
  title: "News | PokerGO",
  ogTitle: "News | PokerGO",
  twitterTitle: "News | PokerGO",
});

definePageMeta({
  layout: "news",
});

const PAGE_SIZE = 18;

const articlesList = ref<Article[]>([]);
const loading = ref(true);
const hasMore = ref(false);

const searchSuggestions = ref<string[]>([]);
const autoCompleteLoading = ref(false);
const searchQuery = ref("");
const querySessionId = ref("");
const queryId = ref("");

const { data: initialData, pending } = await useAsyncData(
  () =>
    repository.articles.getAllArticles({
      pagination: {
        pageOffset: 0,
        pageSize: PAGE_SIZE,
      },
      query: searchQuery.value,
    }),
  {
    transform: (data) => {
      return {
        articlesList: data.data,
        hasMore: data.data.length > 0,
      };
    },
    lazy: true,
  },
);

watch(
  () => initialData.value,
  () => {
    if (initialData.value) {
      articlesList.value = initialData.value.articlesList;
      hasMore.value = initialData.value.hasMore;
      loading.value = false;
    }
  },
  { immediate: true },
);

async function loadMore(initSearch: boolean) {
  loading.value = true;

  if (initSearch) {
    articlesList.value = [];
  }

  try {
    const res = await repository.articles.getAllArticles({
      pagination: {
        pageOffset: articlesList.value.length,
        pageSize: PAGE_SIZE,
      },
      query: searchQuery.value,
    });

    articlesList.value.push(...res.data);
    hasMore.value = res.data.length > 0;
  } catch (e) {
    handleApiError(e);
  }

  loading.value = false;
}

const el = ref<HTMLElement>();

useInfiniteScroll(
  window,
  async () => {
    await loadMore(false);
  },
  {
    distance: 10,
    canLoadMore: () => {
      return !loading.value && hasMore.value;
    },
  },
);

async function handleAutoComplete({ query }: AutoCompleteCompleteEvent) {
  if (query) {
    autoCompleteLoading.value = true;
    searchSuggestions.value =
      await repository.articles.getNewsSearchAutocomplete({
        query,
      });
    autoCompleteLoading.value = false;
  } else {
    searchSuggestions.value = [];
  }
}

async function handleSearchResultsClick(entity: Article, index: number) {
  if (!searchQuery.value) {
    return;
  }

  const payload = {
    sz: `${window.screen.width}x${window.screen.height}`,
    ua: navigator.userAgent,
    sw: searchQuery.value as string,
    pn: "1",
    qsid: querySessionId.value,
    qid: queryId.value,
    cli: `${index + 1}`,
    clurl: `/news/${entity.id}`,
    title: entity.title,
    dt: getDeviceType() || "",
  };

  repository.articles.publishNewsSearchTrack(payload);
}
</script>

<template>
  <div>
    <div class="h-20"></div>
    <div class="m-auto max-w-screen-lg px-5 py-10">
      <Banner page="news" location="header" />
      <div class="mb-5 flex items-center justify-between gap-10">
        <h1 class="text-4xl font-bold">NEWS</h1>
        <div class="relative w-96">
          <font-awesome-icon
            :icon="['fas', 'magnifying-glass']"
            class="absolute left-3 top-1/2 z-20 -translate-y-1/2 transform cursor-pointer text-sm text-white"
            @click="loadMore(true)"
          />
          <AutoComplete
            ref="autoCompleteRef"
            v-model="searchQuery"
            variant="outlined"
            fluid
            size="small"
            :delay="0"
            :loading="autoCompleteLoading"
            :suggestions="searchSuggestions"
            placeholder="Search for news articles..."
            input-class="bg-[#282F35] text-white text-sm rounded border-none pl-10 focus:ring-0 block"
            complete-on-focus
            @complete="handleAutoComplete"
            @keydown.enter="loadMore(true)"
            @option-select="loadMore(true)"
          />
        </div>
      </div>

      <div v-if="pending" class="mt-20 text-center">
        <ProgressSpinner class="!h-20 !w-20" />
      </div>
      <div v-else>
        <div
          v-if="articlesList.length"
          ref="el"
          class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3"
        >
          <div v-for="(article, idx) in articlesList" :key="idx">
            <ArticleCard
              :article="article"
              @click="handleSearchResultsClick(article, idx)"
            />
          </div>
        </div>
        <div v-if="loading" class="my-10 text-center">
          <ProgressSpinner class="!h-20 !w-20" />
        </div>
        <div
          v-else-if="articlesList.length < 1"
          class="mt-10 text-center text-surface-300"
        >
          No news items found
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
